import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Minus, Plus, Loader2 } from 'lucide-react';
import { VegIndicator } from './VegIndicator';
import { PreorderPhoto } from './PreorderPhoto';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QtyStepper = ({ id, qty, onChange, disabled }) => (
  <div className="flex items-center gap-1.5">
    <button type="button" disabled={disabled || qty <= 0} onClick={() => onChange(qty - 1)}
      data-testid={`preorder-qty-minus-${id}`}
      className="h-7 w-7 rounded-full border border-border-light flex items-center justify-center hover:border-primary disabled:opacity-30">
      <Minus className="h-3.5 w-3.5" />
    </button>
    <span data-testid={`preorder-qty-${id}`} className="w-5 text-center text-sm font-semibold">{qty}</span>
    <button type="button" disabled={disabled || qty >= 10} onClick={() => onChange(qty + 1)}
      data-testid={`preorder-qty-plus-${id}`}
      className="h-7 w-7 rounded-full border border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-colors disabled:opacity-30">
      <Plus className="h-3.5 w-3.5" />
    </button>
  </div>
);

export const CustomPreorderPicker = ({ vendorId, mealPeriod, disabled, busy, onSubmit }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState({});

  useEffect(() => {
    setLoading(true);
    setQty({});
    axios.get(`${API}/reservations/vendor-menu/${vendorId}`, { params: { meal_period: mealPeriod }, withCredentials: true })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [vendorId, mealPeriod]);

  const total = items.reduce((s, it) => s + (qty[it.id] || 0) * it.price, 0);
  const count = Object.values(qty).reduce((s, n) => s + n, 0);
  const lines = Object.entries(qty).filter(([, n]) => n > 0).map(([item_id, quantity]) => ({ item_id, quantity }));

  if (loading) return <div className="py-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  if (!items.length) {
    return <p data-testid={`preorder-menu-empty-${mealPeriod}`} className="text-sm text-text-muted py-3">This vendor hasn't added pre-order items for this meal yet.</p>;
  }

  return (
    <div data-testid={`preorder-menu-${mealPeriod}`} className="mt-3">
      <label className="block text-xs text-text-muted mb-1.5">Pick your items</label>
      <ul className="divide-y divide-border-light border border-border-light rounded-xl bg-background/60">
        {items.map((it) => (
          <li key={it.id} data-testid={`preorder-item-${mealPeriod}-${it.id}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
            <div className="min-w-0 flex items-start gap-2.5">
              <PreorderPhoto url={it.image_url} alt={it.name} testId={`preorder-item-photo-${mealPeriod}-${it.id}`} />
              <VegIndicator isVeg={it.is_veg} size="sm" className="mt-1" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-text-primary break-words">{it.name}</p>
                {it.description && <p className="text-xs text-text-muted break-words">{it.description}</p>}
                <p data-testid={`preorder-item-price-${mealPeriod}-${it.id}`} className="text-xs font-semibold text-text-secondary mt-0.5">₹{it.price.toFixed(0)}</p>
              </div>
            </div>
            <QtyStepper id={`${mealPeriod}-${it.id}`} qty={qty[it.id] || 0} disabled={disabled}
              onChange={(n) => setQty((q) => ({ ...q, [it.id]: Math.max(0, Math.min(10, n)) }))} />
          </li>
        ))}
      </ul>
      <button
        onClick={() => onSubmit(lines, total)}
        disabled={disabled || busy || count === 0}
        data-testid={`preorder-pay-btn-${mealPeriod}`}
        className="mt-3 w-full bg-primary hover:bg-primary-hover text-white font-medium py-2.5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? 'Processing...' : count === 0 ? 'Add items to pre-order' : `Pay ₹${total.toFixed(0)} & Reserve (${count})`}
      </button>
    </div>
  );
};
